import { useState } from 'react'
import { Button } from '../../ui/button'
import { Icon } from '../../ui/icon'
import { InputText } from '../../ui/input-text'
import { Select, type SelectOption } from '../../ui/select'

interface CarDetailsScreenProps {
	onNext: (data: Record<string, any>) => void
	onBack: () => void
	data: Record<string, any>
}

export function CarDetailsScreen({
	onNext,
	onBack,
	data,
}: CarDetailsScreenProps) {
	const [formData, setFormData] = useState({
		licensePlate: data.licensePlate || '',
		carYear: data.carYear || '',
		carBrand: data.carBrand || '',
		carModel: data.carModel || '',
		carVersion: data.carVersion || '',
		hasGNC: data.hasGNC || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const carBrands: SelectOption[] = [
		{ value: 'toyota', label: 'Toyota' },
		{ value: 'ford', label: 'Ford' },
		{ value: 'chevrolet', label: 'Chevrolet' },
		{ value: 'volkswagen', label: 'Volkswagen' },
		{ value: 'fiat', label: 'Fiat' },
		{ value: 'renault', label: 'Renault' },
		{ value: 'peugeot', label: 'Peugeot' },
		{ value: 'honda', label: 'Honda' },
		{ value: 'hyundai', label: 'Hyundai' },
		{ value: 'nissan', label: 'Nissan' },
	]

	const carYears: SelectOption[] = Array.from({ length: 30 }, (_, i) => {
		const year = new Date().getFullYear() - i
		return { value: String(year), label: String(year) }
	})

	// Modelos ficticios - en producción esto vendría de una API basada en la marca
	const carModels: SelectOption[] = [
		{ value: 'corolla', label: 'Corolla' },
		{ value: 'civic', label: 'Civic' },
		{ value: 'focus', label: 'Focus' },
		{ value: 'gol', label: 'Gol' },
		{ value: 'onix', label: 'Onix' },
	]

	// Versiones ficticias - en producción esto vendría de una API basada en marca y modelo
	const carVersions: SelectOption[] = [
		{ value: 'base', label: 'Base' },
		{ value: 'comfort', label: 'Comfort' },
		{ value: 'premium', label: 'Premium' },
		{ value: 'sport', label: 'Sport' },
	]

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.licensePlate) newErrors.licensePlate = 'Ingresa la patente'
		if (!formData.carYear) newErrors.carYear = 'Selecciona el año'
		if (!formData.carBrand) newErrors.carBrand = 'Selecciona la marca'
		if (!formData.carModel) newErrors.carModel = 'Selecciona el modelo'
		if (!formData.carVersion) newErrors.carVersion = 'Selecciona la versión'

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.licensePlate &&
		formData.carYear &&
		formData.carBrand &&
		formData.carModel &&
		formData.carVersion

	return (
		<div className="flex w-full flex-col gap-6 pb-20 md:gap-8 md:pb-0">
			{/* Form header */}
			<div className="flex w-full flex-col gap-2 border-b border-neutral-400 pb-4">
				<h2 className="font-parkinsans text-[20px] leading-7 font-medium text-blue-800 md:text-[24px] md:leading-8">
					Sobre tu auto
				</h2>
				<p className="font-parkinsans text-sm leading-6 font-normal text-neutral-900 md:text-base md:leading-[30px]">
					Completá los datos de tu vehículo
				</p>
			</div>

			{/* Form fields */}
			<div className="flex w-full flex-col gap-4 md:gap-3">
				{/* Row 1: Patente + Año */}
				<div className="flex w-full flex-col gap-4 md:flex-row md:gap-8">
					<div className="w-full md:flex-1">
						<InputText
							label="Patente"
							labelPosition="up"
							size="m"
							placeholder="Ingresa la patente"
							value={formData.licensePlate}
							onChange={(e) => {
								setFormData({
									...formData,
									licensePlate: e.target.value.toUpperCase(),
								})
								setErrors({ ...errors, licensePlate: '' })
							}}
							errorText={errors.licensePlate}
							required
						/>
					</div>
					<div className="flex-1">
						<Select
							label="Año"
							labelPosition="up"
							size="m"
							options={carYears}
							value={formData.carYear}
							onValueChange={(value) => {
								setFormData({ ...formData, carYear: value })
								setErrors({ ...errors, carYear: '' })
							}}
							placeholder="Año"
							errorText={errors.carYear}
						/>
					</div>
				</div>

				{/* Row 2: Marca + Modelo */}
				<div className="flex w-full flex-col gap-4 md:flex-row md:gap-8">
					<div className="w-full md:flex-1">
						<Select
							label="Marca"
							labelPosition="up"
							size="m"
							options={carBrands}
							value={formData.carBrand}
							onValueChange={(value) => {
								setFormData({ ...formData, carBrand: value })
								setErrors({ ...errors, carBrand: '' })
							}}
							placeholder="Marca"
							errorText={errors.carBrand}
							searchable
						/>
					</div>
					<div className="w-full md:flex-1">
						<Select
							label="Modelo"
							labelPosition="up"
							size="m"
							options={carModels}
							value={formData.carModel}
							onValueChange={(value) => {
								setFormData({ ...formData, carModel: value })
								setErrors({ ...errors, carModel: '' })
							}}
							placeholder="Modelo"
							errorText={errors.carModel}
						/>
					</div>
				</div>

				{/* Row 3: Versión + Radio buttons GNC */}
				<div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:gap-8">
					<div className="w-full md:flex-1">
						<Select
							label="Versión"
							labelPosition="up"
							size="m"
							options={carVersions}
							value={formData.carVersion}
							onValueChange={(value) => {
								setFormData({ ...formData, carVersion: value })
								setErrors({ ...errors, carVersion: '' })
							}}
							placeholder="Versión"
							errorText={errors.carVersion}
						/>
					</div>
					<div className="flex h-auto w-full items-center gap-4 md:h-[46px] md:flex-1 md:gap-6">
						{/* Radio button: Tiene GNC */}
						<button
							type="button"
							onClick={() => setFormData({ ...formData, hasGNC: 'yes' })}
							className="flex items-center gap-2 p-2"
						>
							<div
								className={`flex size-[18px] items-center justify-center rounded-full border ${
									formData.hasGNC === 'yes'
										? 'border-blue-800'
										: 'border-blue-800'
								} bg-white`}
							>
								{formData.hasGNC === 'yes' && (
									<div className="size-2.5 rounded-full bg-blue-800" />
								)}
							</div>
							<p className="font-parkinsans text-base leading-[30px] font-normal text-blue-900">
								Tiene GNC
							</p>
						</button>

						{/* Radio button: No tiene GNC */}
						<button
							type="button"
							onClick={() => setFormData({ ...formData, hasGNC: 'no' })}
							className="flex items-center gap-2 p-2"
						>
							<div
								className={`flex size-[18px] items-center justify-center rounded-full border ${
									formData.hasGNC === 'no'
										? 'border-blue-800'
										: 'border-blue-800'
								} bg-white`}
							>
								{formData.hasGNC === 'no' && (
									<div className="size-2.5 rounded-full bg-blue-800" />
								)}
							</div>
							<p className="font-parkinsans text-base leading-[30px] font-normal text-blue-900">
								No tiene GNC
							</p>
						</button>
					</div>
				</div>
			</div>

			{/* Footer buttons - Desktop only */}
			<div className="hidden w-full flex-col-reverse items-center justify-between gap-3 md:flex md:flex-row md:gap-0">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="primary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>

			{/* Mobile footer - Fixed at bottom */}
			<div className="fixed right-0 bottom-0 left-0 z-10 border-t border-neutral-400 bg-white px-6 py-3 md:hidden">
				<div className="flex items-center justify-between">
					<Button
						variant="primary"
						appearance="ghost"
						size="m"
						onClick={onBack}
						className="gap-2 rounded-xl px-4 py-3"
					>
						<Icon name="arrow-left" className="size-4" />
						Atrás
					</Button>
					<Button
						variant="primary"
						appearance="solid"
						size="m"
						onClick={handleSubmit}
						disabled={!isFormValid}
						className="gap-2 rounded-xl px-4 py-3"
					>
						Siguiente
						<Icon name="arrow-right" className="size-3.5" />
					</Button>
				</div>
			</div>
		</div>
	)
}
